import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { P, eyebrow, eyebrowLabel, onPhone, shadow } from '../../theme/tokens';

const ROW_HOVER = `
  background: linear-gradient(90deg, rgba(30, 58, 95, 0.14) 0%, rgba(30, 58, 95, 0.07) 45%, rgba(30, 58, 95, 0.015) 100%);
  box-shadow: inset 3px 0 0 ${P.navy};
`;

export const Card = styled.section`
  background: ${P.white};
  border: 1px solid ${P.line};
  border-radius: 12px;
  box-shadow: ${shadow.sm};
`;

export const Head = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 12px;
`;

export const HeadText = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  flex: 1 1 0;

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${P.ink};
  }
  span {
    font-size: 13px;
    color: ${P.subtle};
  }
`;

export const HeadTools = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Gear = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(p) => (p.$open ? P.navy : P.border)};
  background: ${(p) => (p.$open ? P.bgSubtle : P.white)};
  color: ${P.muted};
  font-size: 12px;

  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
  ${onPhone} {
    width: 40px;
    height: 40px;
  }
`;

export const ColMenu = styled.div`
  position: absolute;
  top: 38px;
  right: 0;
  z-index: 35;
  width: 232px;
  background: ${P.white};
  border: 1px solid ${P.border};
  border-radius: 10px;
  box-shadow: ${shadow.md};
  overflow: hidden;
`;

export const ColMenuHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${P.line};

  span {
    ${eyebrowLabel}
  }
  button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    font-size: 11px;
    color: ${P.subtle};
  }
`;

export const ColList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
`;

export const ColOption = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: ${(p) => (p.$locked ? 'default' : 'pointer')};
  font-size: 13px;
  color: ${(p) => (p.$locked ? P.subtle : P.ink)};

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
  input:focus-visible + span {
    outline: 2px solid ${P.navy};
    outline-offset: 2px;
  }
`;

export const Box = styled.span`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$checked ? P.grad : P.white)};
  border: 1px solid ${(p) => (p.$checked ? 'transparent' : P.border)};

  svg {
    font-size: 8px;
    color: ${P.white};
    opacity: ${(p) => (p.$checked ? 1 : 0)};
  }
`;

export const Reset = styled.button`
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: none;
  text-align: center;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${P.navyInk};
  cursor: pointer;
  background: ${P.bgSubtle};
`;

export const Cards = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const CardRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid ${P.line};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  ${(p) => p.$selected && ROW_HOVER}

  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: -2px;
  }
`;

export const CardMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

export const CardLead = styled.div`
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${P.ink};
`;

export const CardMeta = styled.div`
  font-size: 12.5px;
  color: ${P.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardChevron = styled(FontAwesomeIcon)`
  align-self: center;
  flex: none;
  font-size: 12px;
  color: ${P.subtle};
`;

export const Scroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  min-width: ${(p) => p.$minWidth}px;
  border-collapse: collapse;
  font-size: 13px;
`;

export const HeadRow = styled.tr`
  background: ${P.bgSubtle};
`;

export const Th = styled.th`
  text-align: ${(p) => p.$align || 'left'};
  padding: 10px ${(p) => p.$pad}px;
  ${eyebrow}
  border-bottom: 1px solid ${P.line};
  white-space: nowrap;
`;

export const Row = styled.tr`
  transition: background 150ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 150ms ease;
  background: transparent;
  box-shadow: inset 0 0 0 ${P.navy};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  ${(p) => p.$selected && ROW_HOVER}

  &:hover {
    ${ROW_HOVER}
  }
  &:focus-visible {
    outline: 2px solid ${P.navy};
    outline-offset: -2px;
  }
`;

export const Td = styled.td`
  padding: 13px ${(p) => p.$pad}px;
  border-bottom: 1px solid ${P.line};
  vertical-align: top;
  text-align: ${(p) => p.$align || 'left'};
`;

export const Text = styled.span`
  font-size: 13px;
  font-weight: ${(p) => (p.$strong ? 600 : 400)};
  color: ${(p) => (p.$strong ? P.ink : P.muted)};
  white-space: ${(p) => (p.$nowrap ? 'nowrap' : 'normal')};
  font-variant-numeric: ${(p) => (p.$tabular ? 'tabular-nums' : 'normal')};
  ${(p) =>
    p.$ellipsis &&
    `
    display: block;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

export const Sub = styled.div`
  font-size: 11px;
  color: ${P.subtle};
  margin-top: 2px;
`;

export const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

export const Link = styled.a`
  font-weight: 600;
  color: ${P.navyInk};
  cursor: pointer;
  text-decoration: none;
`;

export const Play = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  a {
    font-weight: 600;
    font-size: 12px;
  }
  a svg {
    font-size: 9px;
    margin-right: 5px;
  }
  span {
    font-size: 12px;
    color: ${P.subtle};
  }
`;

const shimmer = keyframes`
  0% { background-position: -320px 0; }
  100% { background-position: 320px 0; }
`;

export const SkeletonBar = styled.div`
  height: 12px;
  width: 70%;
  border-radius: 6px;
  background: linear-gradient(90deg, ${P.gray100} 25%, ${P.gray200} 37%, ${P.gray100} 63%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

export const Empty = styled.div`
  padding: 44px 20px;
  text-align: center;
  color: ${P.subtle};
  font-size: 14px;
`;

export const Pager = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 14px;
  padding: 14px 20px;
  border-top: 1px solid ${P.line};
`;

export const PagerNote = styled.span`
  margin-right: auto;
  font-size: 12px;
  color: ${P.subtle};
`;

export const Pages = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  ${onPhone} {
    flex-wrap: wrap;
  }
`;

export const NavBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${P.muted};
  font-size: 10px;
  ${onPhone} {
    width: 40px;
    height: 40px;
  }

  &:disabled {
    cursor: default;
    color: ${P.border};
  }
`;

export const PageBtn = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: none;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  background: ${(p) => (p.$active ? P.grad : 'transparent')};
  color: ${(p) => (p.$active ? P.white : p.$gap ? P.subtle : P.muted)};
  cursor: ${(p) => (p.$gap ? 'default' : 'pointer')};
  transition: background 150ms ease, color 150ms ease;
  ${onPhone} {
    min-width: 40px;
    height: 40px;
  }
`;

export const SizeSelect = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${P.border};
  border-radius: 999px;
  padding: 5px 14px;

  select {
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${P.ink};
    cursor: pointer;
  }
`;
